/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { User } from "../models/User"
import type { UserImportResult } from "../models/UserImportResult"

import type { CancelablePromise } from "../core/CancelablePromise"
import { OpenAPI } from "../core/OpenAPI"
import { request as __request } from "../core/request"

export class UserService {
  /**
   * Get my own profile API Endpoint
   * Get my own profile
   * @param xAccessToken
   * @returns User successful operation
   * @throws ApiError
   */
  public static getMyProfile(xAccessToken: string): CancelablePromise<User> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/user/profile",
      headers: {
        "x-access-token": xAccessToken,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `No Permission`,
        500: `Internal Server Error`,
      },
    })
  }

  /**
   * Create User API Endpoint
   * Create User
   * @param companyId
   * @param requestBody
   * @param xAccessToken
   * @returns User successful operation
   * @throws ApiError
   */
  public static createUser(
    companyId: number,
    requestBody: {
      employeeId: string
      email?: string
      lastName: string
      firstName: string
      lastNameKana: string
      firstNameKana: string
      gender?: string
      genderComment?: string
      birthday?: string
      postCode?: string
      province?: string
      city?: string
      address?: string
      buildingNo?: string
      phoneNumber?: string
      joiningDate: string
      resignDate?: string
      status: string
      employmentType?: string
      comment?: string
      userType: string
      password: string
      tagIds?: Array<number>
    },
    xAccessToken?: string
  ): CancelablePromise<User> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/company/{companyId}/user",
      path: {
        companyId: companyId,
      },
      headers: {
        "x-access-token": xAccessToken,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `No Permission`,
        500: `Internal Server Error`,
      },
    })
  }

  /**
   * List Users API Endpoint
   * List Users
   * @param xAccessToken
   * @param companyId
   * @param limit
   * @param offset
   * @param orderBy
   * @param sortOrder
   * @param name
   * @param nameCompareType
   * @param id
   * @param idCompareType
   * @param years
   * @param yearsCompareType
   * @param tagIds
   * @param employmentType
   * @param isResigned
   * @returns any successful operation
   * @throws ApiError
   */
  public static listUsers(
    xAccessToken: string,
    companyId: number,
    limit?: number,
    offset?: number,
    orderBy?: string,
    sortOrder?: string,
    name?: string,
    nameCompareType?: string,
    id?: string,
    idCompareType?: string,
    years?: string,
    yearsCompareType?: string,
    tagIds?: Array<number>,
    employmentType?: string,
    isResigned?: boolean
  ): CancelablePromise<{
    users: Array<User>
    count: number
  }> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/company/{companyId}/user",
      path: {
        companyId: companyId,
      },
      headers: {
        "x-access-token": xAccessToken,
      },
      query: {
        limit: limit,
        offset: offset,
        orderBy: orderBy,
        sortOrder: sortOrder,
        name: name,
        nameCompareType: nameCompareType,
        id: id,
        idCompareType: idCompareType,
        years: years,
        yearsCompareType: yearsCompareType,
        tagIds: tagIds,
        employmentType: employmentType,
        isResigned: isResigned,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `No Permission`,
        500: `Internal Server Error`,
      },
    })
  }

  /**
   * Import User API Endpoint
   * Import User
   * @param xAccessToken
   * @param companyId
   * @param formData
   * @returns UserImportResult successful operation
   * @throws ApiError
   */
  public static importUsers(
    xAccessToken: string,
    companyId: number,
    formData: {
      /**
       * password set for users without email address
       */
      password?: string
      file?: Blob
    }
  ): CancelablePromise<Array<UserImportResult>> {
    return __request(OpenAPI, {
      method: "POST",
      url: "/v1/company/{companyId}/user/import",
      path: {
        companyId: companyId,
      },
      headers: {
        "x-access-token": xAccessToken,
      },
      formData: formData,
      mediaType: "multipart/form-data",
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `No Permission`,
        404: `Record Not Found`,
        500: `Internal Server Error`,
      },
    })
  }

  /**
   * Export Users API Endpoint
   * Export Users
   * @param xAccessToken
   * @param companyId
   * @param name
   * @param nameCompareType
   * @param id
   * @param idCompareType
   * @param years
   * @param yearsCompareType
   * @param tagIds
   * @param employmentType
   * @param isResigned
   * @param orderBy
   * @param sortOrder
   * @returns binary successful operation
   * @throws ApiError
   */
  public static exportUsers(
    xAccessToken: string,
    companyId: number,
    name?: string,
    nameCompareType?: string,
    id?: string,
    idCompareType?: string,
    years?: string,
    yearsCompareType?: string,
    tagIds?: Array<number>,
    employmentType?: string,
    isResigned?: boolean,
    orderBy?: string,
    sortOrder?: string
  ): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/company/{companyId}/user/export",
      path: {
        companyId: companyId,
      },
      headers: {
        "x-access-token": xAccessToken,
      },
      query: {
        name: name,
        nameCompareType: nameCompareType,
        id: id,
        idCompareType: idCompareType,
        years: years,
        yearsCompareType: yearsCompareType,
        tagIds: tagIds,
        employmentType: employmentType,
        isResigned: isResigned,
        orderBy: orderBy,
        sortOrder: sortOrder,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `No Permission`,
        500: `Internal Server Error`,
      },
    })
  }

  /**
   * Get User API Endpoint
   * Get User
   * @param xAccessToken
   * @param companyId
   * @param userId
   * @returns User successful operation
   * @throws ApiError
   */
  public static getUser(
    xAccessToken: string,
    companyId: number,
    userId: number
  ): CancelablePromise<User> {
    return __request(OpenAPI, {
      method: "GET",
      url: "/v1/company/{companyId}/user/{userId}",
      path: {
        companyId: companyId,
        userId: userId,
      },
      headers: {
        "x-access-token": xAccessToken,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `No Permission`,
        404: `Not Found`,
      },
    })
  }

  /**
   * Update User API Endpoint
   * Update User
   * @param companyId
   * @param userId
   * @param requestBody
   * @param xAccessToken
   * @returns User successful operation
   * @throws ApiError
   */
  public static updateUser(
    companyId: number,
    userId: number,
    requestBody: {
      lastName?: string
      firstName?: string
      lastNameKana?: string
      firstNameKana?: string
      gender?: string
      genderComment?: string
      birthday?: string
      postCode?: string
      province?: string
      city?: string
      address?: string
      buildingNo?: string
      phoneNumber?: string
      joiningDate?: string
      resignDate?: string
      status?: string
      employmentType?: string
      tagIds?: Array<number>
      comment?: string
      userType?: string
      firstLoginStatus?: boolean
    },
    xAccessToken?: string
  ): CancelablePromise<User> {
    return __request(OpenAPI, {
      method: "PUT",
      url: "/v1/company/{companyId}/user/{userId}",
      path: {
        companyId: companyId,
        userId: userId,
      },
      headers: {
        "x-access-token": xAccessToken,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `No Permission`,
        500: `Internal Server Error`,
      },
    })
  }

  /**
   * Delete User API Endpoint
   * Delete User
   * @param xAccessToken
   * @param companyId
   * @param userId
   * @returns void
   * @throws ApiError
   */
  public static deleteUser(
    xAccessToken: string,
    companyId: number,
    userId: number
  ): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: "DELETE",
      url: "/v1/company/{companyId}/user/{userId}",
      path: {
        companyId: companyId,
        userId: userId,
      },
      headers: {
        "x-access-token": xAccessToken,
      },
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `No Permission`,
        404: `Not Found`,
      },
    })
  }

  /**
   * Update User Password API Endpoint
   * Update User Password
   * @param companyId
   * @param userId
   * @param requestBody
   * @param xAccessToken
   * @returns void
   * @throws ApiError
   */
  public static updateUserPassword(
    companyId: number,
    userId: number,
    requestBody: {
      password?: string
    },
    xAccessToken?: string
  ): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: "PUT",
      url: "/v1/company/{companyId}/user/password/{userId}",
      path: {
        companyId: companyId,
        userId: userId,
      },
      headers: {
        "x-access-token": xAccessToken,
      },
      body: requestBody,
      mediaType: "application/json",
      errors: {
        400: `Bad Request`,
        401: `Unauthorized`,
        403: `No Permission`,
        500: `Internal Server Error`,
      },
    })
  }
}
